import { Capacitor } from '@capacitor/core'
import { db } from '@core/db/db'
import { META_DEVICE_TOKEN } from '@core/db/schema'
import { CLOUD_API } from '@core/pages/admin/shared'
import { getAtolDiagnostics } from '@core/native/atolDiagnostics'
import { startRemotePolling, stopRemotePolling } from '@core/native/remotePolling'
import { syncService } from '@core/services/sync/SyncService'

interface RemoteCommand {
  id: string
  kind: string
  payload: unknown
  expires_at: string
}

const POLL_INTERVAL_MS = 2000

/**
 * Remote support MVP: касса сама опрашивает backend и выполняет только
 * allowlist-команды. Никакого входящего порта и произвольного shell.
 *
 * Сейчас работает, пока приложение открыто; foreground service — следующий этап.
 */
class RemoteCommandService {
  private timer: number | null = null
  private nativeBootstrapTimer: number | null = null
  private polling = false
  private stopped = false
  private cooldownUntil = 0

  public start(): void {
    if (this.stopped === false && (this.timer != null || this.nativeBootstrapTimer != null)) return
    this.stopped = false
    if (Capacitor.isNativePlatform()) {
      void this.bootstrapNative()
      return
    }
    void this.poll()
    this.timer = window.setInterval(() => void this.poll(), POLL_INTERVAL_MS)
  }

  public stop(): void {
    this.stopped = true
    if (this.timer != null) {
      window.clearInterval(this.timer)
      this.timer = null
    }
    if (this.nativeBootstrapTimer != null) {
      window.clearTimeout(this.nativeBootstrapTimer)
      this.nativeBootstrapTimer = null
    }
    if (Capacitor.isNativePlatform()) {
      void stopRemotePolling().catch(() => undefined)
    }
  }

  /** На Android polling живёт в native foreground service, а не в WebView. */
  private async bootstrapNative(): Promise<void> {
    if (this.stopped) return
    const tokenEntry = await db.meta.get(META_DEVICE_TOKEN)
    const token = typeof tokenEntry?.value === 'string' ? tokenEntry.value : ''
    if (!token) {
      this.nativeBootstrapTimer = window.setTimeout(() => void this.bootstrapNative(), 5000)
      return
    }
    try {
      const deviceId = await syncService.ensureDeviceId()
      await startRemotePolling({
        apiUrl: CLOUD_API,
        token,
        deviceId,
        appVersion: import.meta.env.VITE_APP_VERSION ?? '0.1.0'
      })
    } catch (error) {
      console.warn('[RemoteCommandService] native start failed:', error)
      this.nativeBootstrapTimer = window.setTimeout(() => void this.bootstrapNative(), 30_000)
    }
  }

  private async poll(): Promise<void> {
    if (this.stopped || this.polling || Date.now() < this.cooldownUntil) return
    this.polling = true
    try {
      const tokenEntry = await db.meta.get(META_DEVICE_TOKEN)
      const token = typeof tokenEntry?.value === 'string' ? tokenEntry.value : ''
      if (!token) return

      const deviceId = await syncService.ensureDeviceId()
      const response = await fetch(`${CLOUD_API}/remote/poll`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          device_id: deviceId,
          app_version: import.meta.env.VITE_APP_VERSION ?? '0.1.0',
          capabilities: {
            diagnostics: true,
            kktStatus: false,
            kktSelfTest: false,
            testPrint: false
          }
        })
      })

      // Старый device-token без scope remote: ждём повторного входа в облако,
      // чтобы не долбить сервер каждые 2 секунды.
      if (response.status === 403) {
        this.cooldownUntil = Date.now() + 30_000
        return
      }
      if (response.status === 204 || !response.ok) return

      const data = (await response.json()) as { command?: RemoteCommand }
      if (!data.command) return
      await this.execute(data.command, token, deviceId)
    } catch (error) {
      console.warn('[RemoteCommandService] poll failed:', error)
    } finally {
      this.polling = false
    }
  }

  private async execute(
    command: RemoteCommand,
    token: string,
    deviceId: string
  ): Promise<void> {
    let status: 'done' | 'failed' = 'done'
    let result: unknown = null
    let error: string | undefined

    try {
      if (command.kind === 'diagnostics') {
        result = await getAtolDiagnostics()
        if ((result as { error?: string }).error) {
          status = 'failed'
          error = (result as { error?: string }).error
        }
      } else {
        status = 'failed'
        error = `unsupported_remote_command: ${command.kind}`
      }
    } catch (err) {
      status = 'failed'
      error = err instanceof Error ? err.message : String(err)
    }

    try {
      await fetch(`${CLOUD_API}/remote/result`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          command_id: command.id,
          device_id: deviceId,
          status,
          result,
          error
        })
      })
    } catch (err) {
      console.warn('[RemoteCommandService] result upload failed:', err)
    }
  }
}

export const remoteCommandService = new RemoteCommandService()
